/**
 * House-made ingredients (#180, D21): batches, syrups, juices, creams, and
 * in a kitchen stocks and sauces. An ingredient with a `build`.
 *
 * A recipe's lines are what is served. A line that pours a house-made
 * ingredient pours it as one thing — 50 mL of H.S.L Special Batch — and
 * the batch's own build says what that is made of. So:
 *
 *   - **cost and strength** of a house-made ingredient are its build's
 *     (`batchCosts`), a batch inside a batch first, written onto the
 *     ingredient when it is saved or anything inside it changes price. Every
 *     reader of an ingredient keeps reading one number from one place and
 *     need not know what a batch is;
 *   - **from scratch** is a build with its house-made lines opened up into
 *     what they are made of (`expandBatches`): how a drink is shown from
 *     scratch, and what its flavour is read from;
 *   - **stock**: a sale takes the lines; a house-made ingredient the venue
 *     holds no level for opens into its build (pos.ts). Making one takes its
 *     build off the shelf and adds it (`batchComponents`; the API's route).
 *
 * Everything here is pure.
 */
import { buildTotals } from "./calc.js";
export const MAX_BATCH_YIELD_ML = 100_000;
export const MAX_SHELF_LIFE_DAYS = 3650;
export const MAX_BATCH_RUN_ML = 100_000;
/**
 * The most one line of a drink may pour. Two litres is already absurd for a
 * single measure and is there to catch a decimal point in the wrong place.
 *
 * A house-made build is not a drink and is not held to it: a line of one is
 * part of a batch, and 2.1 L of rye in a 3 L Manhattan blend is an ordinary
 * Tuesday. A build line is capped at the batch yield instead, because a
 * build cannot sensibly make more than a batch may hold.
 */
export const MAX_RECIPE_LINE_ML = 2000;
/** Whether an ingredient is made in-house rather than bought. */
export function isHouseMade(ing) {
    return ing?.build !== undefined && ing.build !== null;
}
/** The volume of a build, millilitres: what its lines add up to. */
export function buildVolumeMl(lines) {
    return lines.reduce((sum, l) => sum + (l.ml > 0 ? l.ml : 0), 0);
}
/**
 * A house-made ingredient whose build can be made: it has lines that add up
 * to something. Fresh lemon juice is house-made with no build at all.
 */
export function hasBuild(ing) {
    return ing !== undefined && isHouseMade(ing) && buildVolumeMl(ing.build.lines) > 0;
}
/**
 * What every house-made ingredient with a build costs a millilitre and how
 * strong it is, from its build.
 *
 * A batch inside a batch is costed from *its* build first, so a price change
 * to a bottle reaches every batch above it. One that (wrongly) contains
 * itself is left at its stored figures rather than looping. Those with no
 * build are left out: their stored cost stands.
 */
export function batchCosts(ingredientsById) {
    const done = new Map();
    const visiting = new Set();
    const costOf = (id) => {
        const settled = done.get(id);
        if (settled)
            return settled;
        const ing = ingredientsById.get(id);
        if (!hasBuild(ing) || visiting.has(id))
            return { costPerMl: ing?.costPerMl ?? 0, abv: ing?.abv ?? 0 };
        visiting.add(id);
        const lines = ing.build.lines.map((line) => {
            const inner = costOf(line.ingredientId);
            return { ingredientId: line.ingredientId, ml: line.ml, costPerMl: inner.costPerMl, abv: inner.abv };
        });
        visiting.delete(id);
        const totals = buildTotals(lines, 0);
        const cost = { costPerMl: totals.volumeMl > 0 ? totals.cost / totals.volumeMl : 0, abv: totals.abv };
        done.set(id, cost);
        return cost;
    };
    for (const [id, ing] of ingredientsById)
        if (hasBuild(ing))
            costOf(id);
    return done;
}
/**
 * The house-made ingredients that have this ingredient in them, directly or
 * through another: the ones whose cost moves when its cost does. The
 * ingredient itself is included when it is house-made with a build.
 */
export function batchesContaining(ingredientId, ingredientsById) {
    const built = [...ingredientsById.entries()].filter((entry) => hasBuild(entry[1]));
    const hit = new Set(built.some(([id]) => id === ingredientId) ? [ingredientId] : []);
    const wanted = new Set([ingredientId]);
    // Each pass adds the batches that pour something already known to be
    // affected; it stops when a pass adds nothing.
    for (let grew = true; grew;) {
        grew = false;
        for (const [id, batch] of built) {
            if (hit.has(id))
                continue;
            if (batch.build.lines.some((l) => wanted.has(l.ingredientId))) {
                hit.add(id);
                wanted.add(id);
                grew = true;
            }
        }
    }
    return hit;
}
/**
 * Why a build cannot be saved onto `ingredientId`, or null when it can: a
 * batch cannot be made out of itself, directly or through another batch.
 */
export function batchProblem(ingredientId, lines, ingredientsById) {
    const seen = new Set();
    const reaches = (id) => {
        if (id === ingredientId)
            return true;
        if (seen.has(id))
            return false;
        seen.add(id);
        const inner = ingredientsById.get(id);
        return hasBuild(inner) && inner.build.lines.some((l) => reaches(l.ingredientId));
    };
    return lines.some((l) => reaches(l.ingredientId)) ? "A batch cannot contain itself, directly or through another batch" : null;
}
/**
 * A build with every house-made line in it opened up into what it is made
 * of, all the way down, and the same ingredient added together.
 *
 * It is how a drink is shown from scratch, and what its flavour is read
 * from. A house-made ingredient with no build stays as itself. Units are
 * dropped: the result is millilitres of each thing, in the order first met.
 */
export function expandBatches(lines, ingredientsById) {
    const totals = new Map();
    const walk = (ingredientId, ml, path) => {
        if (!(ml > 0))
            return;
        const batch = ingredientsById.get(ingredientId);
        if (hasBuild(batch) && !path.has(ingredientId)) {
            const volume = buildVolumeMl(batch.build.lines);
            const inner = new Set(path).add(ingredientId);
            for (const line of batch.build.lines)
                walk(line.ingredientId, (line.ml / volume) * ml, inner);
            return;
        }
        totals.set(ingredientId, (totals.get(ingredientId) ?? 0) + ml);
    };
    for (const line of lines)
        walk(line.ingredientId, line.ml, new Set());
    return [...totals.entries()].map(([ingredientId, ml]) => ({ ingredientId, ml }));
}
/** A build as spec lines, from scratch: `expandBatches` in the shape the screens draw. */
export function fromScratchLines(lines, ingredientsById) {
    return expandBatches(lines, ingredientsById).map((l) => ({ ingredientId: l.ingredientId, ml: Math.round(l.ml * 10) / 10, unit: "ml" }));
}
/**
 * Why a build cannot be trusted for a cost: the lines that have no amount,
 * or pour something with no cost on file. Empty when it is fully costed.
 */
export function costingGaps(lines, ingredientsById) {
    const gaps = [];
    for (const line of lines) {
        const ing = ingredientsById.get(line.ingredientId);
        if (!ing) {
            gaps.push(`${line.ingredientId} is not in the library`);
            continue;
        }
        if (ing.kind === "dilution")
            continue;
        if (!(line.ml > 0) && line.unit !== "top")
            gaps.push(`${ing.name} has no amount`);
        // A confirmed zero is a real cost (water); an unconfirmed one is a gap.
        else if (!(ing.costPerMl > 0) && !ing.reviewed)
            gaps.push(`${ing.name} has no cost`);
    }
    return gaps;
}
/**
 * What making `volumeMl` of a house-made ingredient takes: its build scaled
 * to that volume. One level only: a batch that pours another batch takes
 * that batch off the shelf, not the bottles inside it.
 */
export function batchComponents(ing, volumeMl) {
    if (!hasBuild(ing) || !(volumeMl > 0))
        return [];
    const volume = buildVolumeMl(ing.build.lines);
    const totals = new Map();
    for (const line of ing.build.lines) {
        if (!(line.ml > 0))
            continue;
        totals.set(line.ingredientId, (totals.get(line.ingredientId) ?? 0) + (line.ml / volume) * volumeMl);
    }
    return [...totals.entries()].map(([ingredientId, ml]) => ({ ingredientId, ml: Math.round(ml * 10) / 10 }));
}
/** The recipes that pour this ingredient, by name. */
export function batchUses(ingredientId, recipes) {
    return recipes
        .map((r) => ({
        specId: r.specId,
        name: r.name,
        pourMl: r.lines.filter((l) => l.ingredientId === ingredientId).reduce((sum, l) => sum + l.ml, 0),
    }))
        .filter((u) => u.pourMl > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
}
/** When a batch made at `madeAt` runs out, or null when it has no shelf life. */
export function batchExpiry(madeAt, shelfLifeDays) {
    if (!shelfLifeDays || !(shelfLifeDays > 0))
        return null;
    const made = Date.parse(madeAt);
    if (!Number.isFinite(made))
        return null;
    return new Date(made + shelfLifeDays * 86_400_000).toISOString();
}
/** How long before its expiry a batch is called out as nearly gone. */
export const BATCH_EXPIRING_HOURS = 24;
/** Where a made batch is in its shelf life. Unknown without an expiry. */
export function batchFreshness(expiresAt, now = new Date()) {
    if (!expiresAt)
        return "unknown";
    const left = Date.parse(expiresAt) - now.getTime();
    if (!Number.isFinite(left))
        return "unknown";
    if (left <= 0)
        return "expired";
    return left <= BATCH_EXPIRING_HOURS * 3_600_000 ? "expiring" : "fresh";
}
/**
 * Every house-made ingredient worth showing at a venue, most urgent first:
 * expired, then nearly expired, then the fewest serves left, then by name.
 *
 * Worth showing when a recipe on the list pours it, or the venue holds some.
 */
export function batchStatuses(input) {
    const now = input.now ?? new Date();
    const latest = new Map();
    for (const run of input.runs) {
        const held = latest.get(run.batchId);
        if (!held || run.madeAt > held.madeAt)
            latest.set(run.batchId, run);
    }
    const recipes = input.onMenuSpecIds ? input.recipes.filter((r) => input.onMenuSpecIds.has(r.specId)) : input.recipes;
    const rank = { expired: 0, expiring: 1, fresh: 2, unknown: 2 };
    return input.ingredients
        .filter((ing) => isHouseMade(ing))
        .map((ing) => {
        const uses = batchUses(ing.ingredientId, recipes);
        const heldMl = input.heldMlByIngredientId.get(ing.ingredientId) ?? null;
        const biggestPour = Math.max(0, ...uses.map((u) => u.pourMl));
        const lastRun = latest.get(ing.ingredientId) ?? null;
        return {
            batchId: ing.ingredientId,
            name: ing.name,
            build: ing.build ?? null,
            hasBuild: hasBuild(ing),
            heldMl,
            servesLeft: heldMl !== null && biggestPour > 0 ? Math.max(0, Math.floor(heldMl / biggestPour)) : null,
            uses,
            lastRun,
            // Nothing on the shelf has nothing to go off.
            freshness: heldMl !== null && heldMl <= 0 ? "unknown" : batchFreshness(lastRun?.expiresAt, now),
        };
    })
        .filter((b) => b.uses.length > 0 || (b.heldMl ?? 0) > 0)
        .sort((a, b) => rank[a.freshness] - rank[b.freshness] ||
        (a.servesLeft ?? Infinity) - (b.servesLeft ?? Infinity) ||
        a.name.localeCompare(b.name));
}
//# sourceMappingURL=recipes.js.map